import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import {
  loadAlertMessages,
  setLastMessagesLoadDate,
  refreshAlertMessages,
} from '../../redux/actions/messagesActions';
import MessagesMessageListItem from './MessagesMessageListItem';
import sort from '../../utilities/sort';
import MessagesMessageListFilterBar from './MessagesMessageListFilterBar';
import {
  Button,
  buttonSizes,
  buttonTypes,
  buttonStyles,
  buttonTextSizes,
} from '../shared/Button';
import { FormattedMessage } from 'react-intl';

/**
 * The display component for the list of messages
 * @param {object} openMessage - The information regarding the currently selected message
 * @param {function} viewMessage - Function to select which message to view in the full view
 */

export function UnconnectedMessagesMessageList(props) {
  const {
    messages,
    lastMessagesLoadDate,
    openMessage,
    viewMessage,
    language,
  } = props;
  const [messagesFilters, setMessagesFilters] = useState({
    sortParam: 'startTime',
    sortOrder: 'za',
    active: true,
    completed: true,
    cancelled: true,
    cleared: true,
    dateFrom: null,
    dateTo: null,
  });
  const [messagesFilterBar, setMessagesFilterBar] = useState({
    filterMenuExpanded: false,
    lastMessagesLoadDate: null,
  });

  const loadNext60DaysText = (
    <FormattedMessage
      id="messageCenter.loadNext60DaysText"
      defaultMessage="Load the previous 60 days"
      description="Help text."
    />
  );
  const loadNext6MonthsText = (
    <FormattedMessage
      id="messageCenter.loadNext6MonthsText"
      defaultMessage="Load the previous 6 months"
      description="Help text."
    />
  );

  /**
   * Function to set which filters are in use for the displayed table of responses
   * @param {object} event - The click event for selecting the filter options
   * @fires setState - Updates local state with the filter information
   */
  function setFilterType(event) {
    const value = event.target.name;
    let filters = messagesFilters;
    // Check to see if the filter value is set to true, if so it is set to false. If it's not true, set it to true
    if (filters[value]) {
      setMessagesFilters({
        ...messagesFilters,

        [value]: false,
      });
    } else {
      setMessagesFilters({
        ...messagesFilters,

        [value]: true,
      });
    }
  }

  /**
   * Generic function for setting local state with the selected sort settings
   * @param {object} event - The click event for the sort options
   * @fires setState - Updates local state with the sort settings
   */
  function setSortType(event) {
    const value = event.target.value;
    let sortOrder = messagesFilters.sortOrder;
    if (value === messagesFilters.sortParam) {
      if (messagesFilters.sortOrder === 'az') {
        sortOrder = 'za';
      } else {
        sortOrder = 'az';
      }
    }

    setMessagesFilters({
      ...messagesFilters,
      sortParam: value,
      sortOrder: sortOrder,
    });
  }

  /**
   * Function to reset the values of the search/filter bar to display everything
   * @fires setState - Returns state to its default settings
   */
  function resetFilters() {
    setMessagesFilters({
      ...messagesFilters,

      sortParam: 'startTime',
      sortOrder: 'za',
      active: true,
      completed: true,
      cancelled: true,
      cleared: true,
      dateFrom: null,
      dateTo: null,
    });
  }

  /**
   * Function to expand or compact the filter menu by setting state
   * @fires setState - Swaps the state setting between true and false
   */
  function filterMenuToggle() {
    setMessagesFilterBar({
      filterMenuExpanded: !messagesFilterBar.filterMenuExpanded,
    });
  }

  /**
   * Function to generate the display of the messages
   * @returns {Array} - The sorted list of messages
   */
  function messageDisplay() {
    // Take in full message list as an object and convert it to an array
    const rawMessages = Object.values(messages);
    let messageList = [];
    // Create a new array of just the parts of the original array that are desired
    rawMessages.map((message) => {
      messageList.push({
        msgId: message.msgId,
        type: message.type,
        title: message.title,
        startTime: new Date(message.startTime),
        recipientCount: message.recipientCount,
        message: message.message,
        messageStatus: message.messageStatus,
      });
      return '';
    });

    let dateRangeList = [];
    let startDate;
    if (messagesFilters.dateFrom) {
      startDate = new Date(messagesFilters.dateFrom);
    }
    let endDate;
    if (messagesFilters.dateTo) {
      endDate = new Date(messagesFilters.dateTo);
      endDate.setUTCDate(endDate.getUTCDate() + 1);
    }
    const lastDate = new Date(lastMessagesLoadDate);
    const dayDifference = Math.round((endDate - lastDate) / 86400000);
    // This is for the date range filtering
    // If the user enters a start date that is earlier than the earliest date that has been fetched from the server, this will send off an api call that will get all messages as far back as the date given
    if (startDate) {
      if (startDate < lastDate) {
        props.dispatch(setLastMessagesLoadDate(startDate));
        props.dispatch(loadAlertMessages(startDate));
      }
      if (endDate) {
        dateRangeList = messageList.filter(
          (item) => item.startTime >= startDate && item.startTime <= endDate
        );
      } else {
        dateRangeList = messageList.filter(
          (item) => item.startTime >= startDate
        );
      }
    } else if (endDate) {
      if (endDate < lastDate || dayDifference < 59) {
        const newDate = new Date(endDate);
        newDate.setUTCDate(endDate.getUTCDate() - 60);
        props.dispatch(setLastMessagesLoadDate(newDate));
        props.dispatch(loadAlertMessages(newDate));
      }
      dateRangeList = messageList.filter((item) => item.startTime <= endDate);
    } else {
      dateRangeList = messageList;
    }
    // These filter based on status
    let active = [];
    if (messagesFilters.active) {
      active = dateRangeList.filter((item) => item.messageStatus === 'Active');
    }

    let completed = [];
    if (messagesFilters.completed) {
      completed = dateRangeList.filter(
        (item) => item.messageStatus === 'Completed'
      );
    }

    let cancelled = [];
    if (messagesFilters.cancelled) {
      cancelled = dateRangeList.filter(
        (item) => item.messageStatus === 'Cancelled'
      );
    }

    let cleared = [];
    if (messagesFilters.cleared) {
      cleared = dateRangeList.filter(
        (item) => item.messageStatus === 'Cleared'
      );
    }
    // Takes the individual arrays of filtered messages and recombines them
    // This way if any of those arrays are empty, the messages with that status will not show up in the array
    let filteredMessages = [...active, ...completed, ...cancelled, ...cleared];

    // Sends the filtered array to the sort function to be sorted by the appropriate parameter based on the selected item in the sort bar
    const orderedMessages = sort(
      filteredMessages,
      messagesFilters.sortParam,
      messagesFilters.sortOrder
    );
    let displayMessages = orderedMessages.map((message, index) => {
      return (
        <MessagesMessageListItem
          message={message}
          index={index}
          key={index}
          openMessage={openMessage}
          viewMessage={viewMessage}
          data-test={message.title}
        />
      );
    });
    return displayMessages;
  }

  /**
   * Function to set the from date for filtering messages by date
   * @fires setState - Sets the from date for filtering messages
   */
  function setFilterFromDate(date) {
    setMessagesFilters({
      ...messagesFilters,

      dateFrom: date,
    });
  }

  /**
   * Function to set the to date for filtering messages by date
   * @fires setState - Sets the to date for filtering messages
   */
  function setFilterToDate(date) {
    setMessagesFilters({
      ...messagesFilters,

      dateTo: date,
    });
  }

  /**
   * Function to retrieve 60 days more of messages
   * @description - Takes in the last date that was retrieved from the api and is stored in local state then subtracts 60 days from that date. The resulting date is sent as the new date to retrieve messages from and stored in local state.
   * @fires dispatch#loadAlertMessages - Triggers the api call to get more messages
   * @fires dispatch#setLastMessagesLoadDate - Updates redux state with the last date messages were retrieved for
   */
  function loadSixtyMoredays() {
    const lastDate = lastMessagesLoadDate;
    const newDate = new Date(lastDate);
    newDate.setUTCDate(lastDate.getUTCDate() - 60);
    props.dispatch(setLastMessagesLoadDate(newDate));
    props.dispatch(loadAlertMessages(newDate));
  }

  /**
   * Function to retrieve 6 months more of messages
   * @description - Takes in the last date that was retrieved from the api and is stored in local state then subtracts 6 months from that date. The resulting date is sent as the new date to retrieve messages from and stored in local state.
   * @fires dispatch#loadAlertMessages - Triggers the api call to get more messages
   * @fires dispatch#setLastMessagesLoadDate - Updates redux state with the last date messages were retrieved for
   */
  function loadSixMoreMonths() {
    const lastDate = lastMessagesLoadDate;
    const newDate = new Date(lastDate);
    newDate.setUTCMonth(lastDate.getUTCMonth() - 6);
    props.dispatch(setLastMessagesLoadDate(newDate));
    props.dispatch(loadAlertMessages(newDate));
  }

  let interval = useRef();

  useEffect(() => {
    // Create a new date object for the currend date
    let startDate = new Date();
    // Check redux state to see if there's a date already in place for loading messages
    if (lastMessagesLoadDate) {
      // If there is, set the start date to that date
      startDate = new Date(lastMessagesLoadDate);
    } else {
      // If not, use the base date
      // Subtract 60 days from the date to get the initial list of messages to display
      startDate.setDate(startDate.getDate() - 60);
    }
    // Trigger loading messages based on the dates
    props.dispatch(loadAlertMessages(startDate));

    // Update Redux state with the last date used to load messages
    props.dispatch(setLastMessagesLoadDate(startDate));
  }, []); // eslint-disable-line

  /**
   * Function to trigger refreshing the details results from the api for the last 7 days
   * @fires dispatch#loadMessageDetails - Triggers the api call
   * @fires dispatch#setLastMessagesLoadDate - Updates redux state with the last date messages were retrieved for
   * @description - This function is used to fire every 2 seconds to update the most recent messages to keep their status current.
   */
  function refreshResults() {
    // Create a date object for the current date
    const today = new Date();
    // Subtract 7 days from the date to get the initial list of messages to display
    today.setDate(today.getDate() - 7);
    props.dispatch(refreshAlertMessages(today));
  }

  useEffect(() => {
    // Start refreshing the last 7 days of messages every 30 seconds
    interval.current = setInterval(refreshResults, 30000);
    return () => {
      clearTimeout(interval.current);
    };
  }, []); // eslint-disable-line

  return (
    <div
      className="message-center__message-list-container"
      data-test="message-center-layout">
      <MessagesMessageListFilterBar
        currentFilters={messagesFilters}
        resetFilters={resetFilters}
        setFilterType={setFilterType}
        setSortType={setSortType}
        filterMenuExpanded={messagesFilterBar.filterMenuExpanded}
        filterMenuToggle={filterMenuToggle}
        setFilterFromDate={setFilterFromDate}
        setFilterToDate={setFilterToDate}
        language={language}
      />
      <div className="message-center__message-list">
        {messageDisplay()}
        <div className="message-list-item__load-more-results-container">
          <h2 className="no-margin text-large text-bold">
            <FormattedMessage
              id="messageCenter.endOfResults"
              defaultMessage="You've reached the end of the results."
              description="Help text."
            />
          </h2>
          <div className="message-list-item__load-more-results-button-container">
            <Button
              text={loadNext60DaysText}
              buttonType={buttonTypes.secondary}
              buttonStyle={buttonStyles.flex}
              buttonTextSize={buttonTextSizes.small}
              buttonSize={buttonSizes.small}
              onClick={loadSixtyMoredays}
              data-test="load-sixty-days"
            />
            <Button
              text={loadNext6MonthsText}
              buttonType={buttonTypes.secondary}
              buttonStyle={buttonStyles.flex}
              buttonTextSize={buttonTextSizes.small}
              buttonSize={buttonSizes.small}
              onClick={loadSixMoreMonths}
              data-test="load-six-months"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    allMessages: state.messages,
    messages: state.messages.alertMessages,
    lastMessagesLoadDate: state.messages.lastMessagesLoadDate,
    language: state.multilingual.selectedLanguage,
  };
};

export default connect(mapStateToProps)(UnconnectedMessagesMessageList);
